import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";

import { SupportTicketAttachment } from "../../services/supportTicketService";

const isImageMime = (mime: string): boolean =>
  mime.startsWith("image/") ||
  ["png", "jpg", "jpeg", "gif", "webp"].some((ext) =>
    mime.toLowerCase().includes(ext)
  );

const isPdfMime = (mime: string): boolean =>
  mime.toLowerCase().includes("pdf") || mime.toLowerCase() === "application/pdf";

const isPreviewableMime = (mime: string): boolean =>
  isImageMime(mime) || isPdfMime(mime);

interface SupportAttachmentListProps {
  ticketId: number;
  attachments?: SupportTicketAttachment[];
  fetchBlob: (ticketId: number, attachmentId: number) => Promise<Blob>;
  onDownload: (attachment: SupportTicketAttachment) => void | Promise<void>;
  downloadingId?: number | null;
}

const SupportAttachmentList: React.FC<SupportAttachmentListProps> = ({
  ticketId,
  attachments,
  fetchBlob,
  onDownload,
  downloadingId = null,
}) => {
  const { t } = useTranslation();
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [previewErrors, setPreviewErrors] = useState<Record<number, boolean>>({});
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const previewableAttachments = useMemo(
    () => (attachments || []).filter((item) => isPreviewableMime(item.mime_type)),
    [attachments]
  );

  useEffect(() => {
    if (!attachments || attachments.length === 0) {
      return;
    }

    let cancelled = false;
    const createdUrls: string[] = [];

    const loadPreviews = async () => {
      for (const attachment of attachments) {
        if (!isPreviewableMime(attachment.mime_type)) {
          continue;
        }

        try {
          const blob = await fetchBlob(ticketId, attachment.id);
          if (cancelled) {
            return;
          }
          const url = URL.createObjectURL(blob);
          createdUrls.push(url);
          setPreviews((current) => ({ ...current, [attachment.id]: url }));
        } catch {
          if (!cancelled) {
            setPreviewErrors((current) => ({ ...current, [attachment.id]: true }));
          }
        }
      }
    };

    void loadPreviews();

    return () => {
      cancelled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [attachments, fetchBlob, ticketId]);

  useEffect(() => {
    if (lightboxIndex === null) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLightboxIndex(null);
        return;
      }

      if (event.key === "ArrowLeft") {
        setLightboxIndex((current) =>
          current === null
            ? null
            : (current - 1 + previewableAttachments.length) %
              previewableAttachments.length
        );
      }

      if (event.key === "ArrowRight") {
        setLightboxIndex((current) =>
          current === null
            ? null
            : (current + 1) % previewableAttachments.length
        );
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxIndex, previewableAttachments.length]);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const lightboxAttachment =
    lightboxIndex !== null ? previewableAttachments[lightboxIndex] : null;
  const lightboxUrl = lightboxAttachment
    ? previews[lightboxAttachment.id]
    : null;
  const lightboxIsPdf = lightboxAttachment
    ? isPdfMime(lightboxAttachment.mime_type)
    : false;

  return (
    <>
      <div className="d-flex flex-column gap-2 mt-2">
        {attachments.map((attachment) => {
          const previewUrl = previews[attachment.id];
          const showImage = isImageMime(attachment.mime_type) && previewUrl;
          const showPdf = isPdfMime(attachment.mime_type) && previewUrl;
          const previewIndex = previewableAttachments.findIndex(
            (item) => item.id === attachment.id
          );

          return (
            <div
              key={attachment.id}
              className="d-flex flex-wrap align-items-start gap-2"
            >
              {showImage ? (
                <button
                  type="button"
                  className="border-0 bg-transparent p-0"
                  onClick={() => setLightboxIndex(previewIndex)}
                  title={t("support.attachments.openPreview")}
                >
                  <img
                    src={previewUrl}
                    alt={attachment.original_filename}
                    style={{
                      maxWidth: 180,
                      maxHeight: 120,
                      objectFit: "contain",
                      borderRadius: 4,
                      border: "1px solid var(--vz-border-color, #e9ebec)",
                      cursor: "zoom-in",
                    }}
                  />
                </button>
              ) : null}
              {showPdf ? (
                <button
                  type="button"
                  className="btn btn-soft-danger btn-sm"
                  onClick={() => setLightboxIndex(previewIndex)}
                  title={t("support.attachments.openPdfPreview")}
                >
                  <i className="ri-file-pdf-2-line me-1" />
                  {t("support.attachments.previewPdf")}
                </button>
              ) : null}
              <Button
                color="soft-secondary"
                size="sm"
                disabled={downloadingId === attachment.id}
                onClick={() => void onDownload(attachment)}
              >
                {downloadingId === attachment.id ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <i className="ri-download-2-line me-1" />
                    {attachment.original_filename}
                    {previewErrors[attachment.id]
                      ? ` (${t("support.attachments.previewUnavailable")})`
                      : null}
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={lightboxIndex !== null && Boolean(lightboxUrl)}
        toggle={() => setLightboxIndex(null)}
        size="xl"
        centered
        className="support-attachment-lightbox"
      >
        <ModalHeader toggle={() => setLightboxIndex(null)}>
          {lightboxAttachment?.original_filename}
          {previewableAttachments.length > 1 && lightboxIndex !== null
            ? ` (${lightboxIndex + 1}/${previewableAttachments.length})`
            : ""}
        </ModalHeader>
        <ModalBody className="text-center bg-dark bg-opacity-10">
          {lightboxAttachment && lightboxUrl ? (
            <>
              <div className="d-flex align-items-center justify-content-center gap-2">
                {previewableAttachments.length > 1 ? (
                  <Button
                    color="light"
                    size="sm"
                    onClick={() =>
                      setLightboxIndex(
                        (lightboxIndex! - 1 + previewableAttachments.length) %
                          previewableAttachments.length
                      )
                    }
                    aria-label={t("support.attachments.prev")}
                  >
                    <i className="ri-arrow-left-line" />
                  </Button>
                ) : null}
                {lightboxIsPdf ? (
                  <iframe
                    title={lightboxAttachment.original_filename}
                    src={lightboxUrl}
                    style={{
                      width: "100%",
                      maxWidth: 900,
                      height: "70vh",
                      border: "1px solid var(--vz-border-color, #e9ebec)",
                      borderRadius: 4,
                      background: "#fff",
                    }}
                  />
                ) : (
                  <img
                    src={lightboxUrl}
                    alt={lightboxAttachment.original_filename}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "75vh",
                      objectFit: "contain",
                    }}
                  />
                )}
                {previewableAttachments.length > 1 ? (
                  <Button
                    color="light"
                    size="sm"
                    onClick={() =>
                      setLightboxIndex(
                        (lightboxIndex! + 1) % previewableAttachments.length
                      )
                    }
                    aria-label={t("support.attachments.next")}
                  >
                    <i className="ri-arrow-right-line" />
                  </Button>
                ) : null}
              </div>
              {previewableAttachments.length > 1 ? (
                <div className="d-flex justify-content-center flex-wrap gap-2 mt-3">
                  {previewableAttachments.map((attachment, index) => {
                    const isActive = index === lightboxIndex;
                    const thumbUrl = previews[attachment.id];
                    const pdf = isPdfMime(attachment.mime_type);

                    return (
                      <button
                        key={attachment.id}
                        type="button"
                        className="border-0 bg-transparent p-0"
                        onClick={() => setLightboxIndex(index)}
                        aria-label={t("support.attachments.thumbnail", {
                          index: index + 1,
                        })}
                        aria-current={isActive ? "true" : undefined}
                      >
                        {pdf || !thumbUrl ? (
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: 4,
                              border: isActive
                                ? "2px solid var(--vz-primary, #405189)"
                                : "1px solid var(--vz-border-color, #e9ebec)",
                              background: "#fff5f5",
                              color: "#f06548",
                            }}
                          >
                            <i className="ri-file-pdf-2-line fs-4" />
                          </span>
                        ) : (
                          <img
                            src={thumbUrl}
                            alt={attachment.original_filename}
                            style={{
                              width: 56,
                              height: 56,
                              objectFit: "cover",
                              borderRadius: 4,
                              border: isActive
                                ? "2px solid var(--vz-primary, #405189)"
                                : "1px solid var(--vz-border-color, #e9ebec)",
                              opacity: isActive ? 1 : 0.75,
                              cursor: "pointer",
                            }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <div className="mt-3">
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => void onDownload(lightboxAttachment)}
                >
                  <i className="ri-download-2-line me-1" />
                  {t("support.attachments.download")}
                </Button>
              </div>
            </>
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
};

export default SupportAttachmentList;
